import React, { useState, useEffect, useRef } from 'react';
import { Search, Package, ExternalLink, Loader2, ArrowUpDown, Layers } from 'lucide-react';

const SUPPLIER_COLORS = {
  Mobilax: { bg: 'bg-orange-500/15', text: 'text-orange-400', border: 'border-orange-500/30', badge: 'bg-orange-500/20 text-orange-400' },
  JensMobiles: { bg: 'bg-blue-500/15', text: 'text-blue-400', border: 'border-blue-500/30', badge: 'bg-blue-500/20 text-blue-400' },
  'LCD-Phone': { bg: 'bg-green-500/15', text: 'text-green-400', border: 'border-green-500/30', badge: 'bg-green-500/20 text-green-400' },
};

const StockBadge = ({ status }) => {
  const isInStock = status?.toLowerCase().includes('stock');
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${isInStock ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
      {status || 'N/A'}
    </span>
  );
};

const SupplierPartSearch = ({ brand, model, onSelect, api }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [viewMode, setViewMode] = useState('compare'); // 'compare' (by price) or 'grouped' (by supplier)
  const ref = useRef(null);
  const timerRef = useRef(null);
  const selectedRef = useRef(false);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current = false;
      return;
    }
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ query, limit: '50' });
        if (brand) params.set('brand', brand);
        if (model) params.set('model', model);
        const resp = await api.get(`/supplier-parts/search?${params}`);
        setResults(resp.data.results || []);
        setOpen(true);
      } catch (err) {
        console.error('Part search error:', err);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(timerRef.current);
  }, [query, brand, model, api]);

  const handleSelect = (part) => {
    selectedRef.current = true;
    onSelect({
      description: part.part_name,
      reference: part.reference || '',
      prix_achat: part.price || 0,
      fournisseur: part.supplier,
    });
    setQuery(part.part_name);
    setOpen(false);
  };

  // Group results by supplier
  const grouped = {};
  results.forEach(r => {
    if (!grouped[r.supplier]) grouped[r.supplier] = [];
    grouped[r.supplier].push(r);
  });

  // Sorted by price for compare view
  const sortedByPrice = [...results].sort((a, b) => a.price - b.price);
  const cheapestPrice = sortedByPrice.length > 0 ? sortedByPrice[0].price : 0;

  const renderPartItem = (part, idx, showSupplierBadge = false, isCheapest = false) => {
    const colors = SUPPLIER_COLORS[part.supplier] || { badge: 'bg-gray-500/20 text-gray-400' };
    return (
      <button
        key={`${part.supplier}-${idx}`}
        type="button"
        onClick={() => handleSelect(part)}
        className={`w-full px-3 py-2.5 text-left hover:bg-white/8 transition-colors border-b border-white/5 last:border-0 group ${isCheapest ? 'bg-emerald-500/5' : ''}`}
        data-testid={`supplier-part-item-${part.supplier}-${idx}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {showSupplierBadge && (
              <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider mr-1.5 ${colors.badge}`}>
                {part.supplier}
              </span>
            )}
            <span className="text-sm text-gray-200 group-hover:text-white leading-tight">{part.part_name}</span>
          </div>
          <div className="flex flex-col items-end shrink-0">
            <span className={`text-sm font-bold whitespace-nowrap ${isCheapest ? 'text-emerald-400' : 'text-cyan-400'}`}>
              {part.price.toFixed(2)} €
            </span>
            {isCheapest && <span className="text-[9px] text-emerald-400 font-medium">Meilleur prix</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <StockBadge status={part.stock_status} />
          {part.reference && (
            <span className="text-[10px] text-gray-500">Ref: {part.reference}</span>
          )}
          {part.url && (
            <a href={part.url} target="_blank" rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="ml-auto text-gray-600 hover:text-cyan-400">
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </button>
    );
  };

  return (
    <div ref={ref} className="relative">
      <label className="block text-sm text-gray-300 mb-1">
        <Search className="inline w-3.5 h-3.5 mr-1 text-cyan-400" />
        Recherche piece fournisseur
      </label>
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          className="input-dark w-full py-2 pl-3 pr-9"
          placeholder={model ? `Rechercher piece ${model}...` : 'Tapez le nom de la piece...'}
          autoComplete="off"
          data-testid="supplier-part-search"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-400 animate-spin" />
        )}
      </div>

      {open && results.length > 0 && (
        <div className="absolute z-[100] w-full mt-1 max-h-80 overflow-auto bg-gray-900 border border-white/15 rounded-xl shadow-2xl" data-testid="supplier-part-results">
          {/* View toggle */}
          <div className="sticky top-0 z-20 flex items-center gap-1 px-2 py-1.5 bg-gray-800/95 backdrop-blur-sm border-b border-white/10">
            <button
              type="button"
              onClick={() => setViewMode('compare')}
              className={`px-2 py-1 rounded text-[10px] font-semibold transition-colors ${viewMode === 'compare' ? 'bg-cyan-500/20 text-cyan-400' : 'text-gray-500 hover:text-gray-300'}`}
              data-testid="view-compare"
            >
              <ArrowUpDown className="inline w-3 h-3 mr-0.5" /> Comparatif
            </button>
            <button
              type="button"
              onClick={() => setViewMode('grouped')}
              className={`px-2 py-1 rounded text-[10px] font-semibold transition-colors ${viewMode === 'grouped' ? 'bg-cyan-500/20 text-cyan-400' : 'text-gray-500 hover:text-gray-300'}`}
              data-testid="view-grouped"
            >
              <Layers className="inline w-3 h-3 mr-0.5" /> Par fournisseur
            </button>
            <span className="ml-auto text-[10px] text-gray-500">{results.length} resultats</span>
          </div>

          {viewMode === 'compare' ? (
            /* Compare view: sorted by price, cheapest first */
            <div>
              {sortedByPrice.map((part, idx) =>
                renderPartItem(part, idx, true, part.price === cheapestPrice)
              )}
            </div>
          ) : (
            /* Grouped view: by supplier */
            Object.entries(grouped).map(([supplier, parts]) => {
              const colors = SUPPLIER_COLORS[supplier] || { bg: 'bg-gray-500/15', text: 'text-gray-400', border: 'border-gray-500/30' };
              return (
                <div key={supplier}>
                  <div className={`sticky top-[34px] z-10 px-3 py-1.5 ${colors.bg} border-b ${colors.border} backdrop-blur-sm flex items-center gap-2`}>
                    <Package className={`w-3.5 h-3.5 ${colors.text}`} />
                    <span className={`text-xs font-bold ${colors.text} uppercase tracking-wider`}>{supplier}</span>
                    <span className="text-xs text-gray-500">({parts.length})</span>
                  </div>
                  {parts.map((part, idx) => renderPartItem(part, idx, false, false))}
                </div>
              );
            })
          )}
        </div>
      )}

      {open && !loading && query.length >= 2 && results.length === 0 && (
        <div className="absolute z-[100] w-full mt-1 bg-gray-900 border border-white/15 rounded-xl shadow-2xl p-4 text-center text-sm text-gray-500" data-testid="supplier-part-no-results">
          Aucune piece trouvee pour "{query}"
        </div>
      )}
    </div>
  );
};

export default SupplierPartSearch;
